import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { getCurrencySymbol } from '../../utils/currency';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';
import alertService from '../../utils/alert';

const USAGE_BAR_COLOR = (pct) => pct > 90 ? 'bg-[#BA0517]' : pct > 70 ? 'bg-[#FE9339]' : 'bg-[#04844B]';

// Same loader SubscriptionPage.jsx uses for the Razorpay subscription
// checkout — which gateway handles a credit-pack purchase is a superadmin
// setting (see ApiCreditController.checkout), so this page has to be able
// to run either flow.
const loadRazorpaySDK = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(window.Razorpay);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error('Failed to load Razorpay SDK'));
    document.head.appendChild(script);
  });

export default function BillingPage() {
  const [searchParams] = useSearchParams();
  const [usage, setUsage] = useState(null);
  const [wl, setWl] = useState({ brandName: '', logoUrl: '', primaryColor: '#3b82f6', accentColor: '#10b981', customDomain: '', supportEmail: '', hideParentBranding: false, markupPercent: 0 });
  const [subTenants, setSubTenants] = useState([]);
  // Deep-linkable — the "API Credits" nav entry points at ?tab=api-credits so
  // it doesn't stay buried behind clicking the right pill after landing here.
  // Plan selection/checkout ("overview"/"plans" tabs) has been removed —
  // Razorpay ("Billing & Plans") is the one real subscription flow now; this
  // page only covers what Razorpay doesn't: AI credits, white-label, sub-tenants.
  const validTabs = ['api-credits', 'white-label', 'sub-tenants'];
  const initialTab = validTabs.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'api-credits';
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState('');
  const [newSub, setNewSub] = useState({ name: '', email: '', domain: '' });
  const [creditBalance, setCreditBalance] = useState(null);
  const [creditPacks, setCreditPacks] = useState([]);
  const [creditTransactions, setCreditTransactions] = useState([]);
  const [buyingPack, setBuyingPack] = useState(null);

  const loadCredits = () => {
    api.get('/billing/api-credits/balance').then(r => setCreditBalance(r.data.data)).catch(() => {});
    api.get('/billing/api-credits/packs').then(r => setCreditPacks(r.data.data || [])).catch(() => {});
    api.get('/billing/api-credits/transactions').then(r => setCreditTransactions(r.data.data?.rows || [])).catch(() => {});
  };

  useEffect(() => {
    api.get('/billing/usage').then(r => setUsage(r.data.data)).catch(() => {});
    api.get('/billing/white-label').then(r => {
      if (r.data.data) setWl(r.data.data);
    });
    api.get('/billing/sub-tenants').then(r => setSubTenants(r.data.data || [])).catch(() => {});
    loadCredits();
    loadRazorpaySDK().catch(() => {});
  }, []);

  // Opens the Razorpay Checkout modal and leaves buyingPack set until the
  // modal actually resolves (success/failure/dismiss) — unlike the Stripe
  // path, opening the modal isn't the end of the flow, so this deliberately
  // does NOT clear buyingPack itself; every exit path below does.
  const openRazorpayCheckout = (data) => {
    if (!window.Razorpay) {
      alertService.error('Error', 'Payment gateway failed to load. Please refresh and try again.');
      setBuyingPack(null);
      return;
    }
    const { orderId, amount, currency, razorpayKeyId, prefill, credits } = data;
    const rzp = new window.Razorpay({
      key: razorpayKeyId,
      amount,
      currency,
      name: 'WaapLeads CRM',
      description: `${credits.toLocaleString()} API Credits`,
      order_id: orderId,
      prefill: { name: prefill?.name || '', email: prefill?.email || '', contact: prefill?.contact || '' },
      theme: { color: '#0176D3' },
      handler: async (response) => {
        try {
          await api.post('/billing/api-credits/verify-razorpay', {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          loadCredits();
        } catch (err) {
          alertService.error('Error', err.response?.data?.error || 'Payment could not be verified. Please contact support if you were charged.');
        } finally {
          setBuyingPack(null);
        }
      },
      modal: { ondismiss: () => setBuyingPack(null) },
    });
    rzp.on('payment.failed', (response) => {
      alertService.error('Error', response.error?.description || 'Payment failed.');
      setBuyingPack(null);
    });
    rzp.open();
  };

  const buyCreditPack = async (pack) => {
    setBuyingPack(pack.credits);
    try {
      const { data } = await api.post('/billing/api-credits/checkout', { credits: pack.credits });
      if (data.data.gateway === 'razorpay') {
        openRazorpayCheckout(data.data);
      } else {
        window.location.href = data.data.url;
      }
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Checkout failed');
      setBuyingPack(null);
    }
  };

  const saveWhiteLabel = async () => {
    setLoading('wl');
    try {
      await api.put('/billing/white-label', wl);
    } finally { setLoading(''); }
  };

  const createSubTenant = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post('/billing/sub-tenants', newSub);
      setSubTenants(st => [...st, data.data]);
      setNewSub({ name: '', email: '', domain: '' });
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to create sub-tenant');
    }
  };

  const usagePct = (used, limit) => limit ? Math.min((used / limit) * 100, 100) : 0;

  // displayCurrency/displayPrice are computed server-side (ApiCreditService.
  // getPacksForTenant): the tenant's own configured rate if set, else a live
  // FX rate, else USD unconverted — never a fabricated number.
  const packDisplay = (pack) => ({
    amount: pack.displayPrice ?? pack.priceUsd,
    currency: pack.displayCurrency || 'USD',
    converted: (pack.displayCurrency || 'USD') !== 'USD',
  });

  return (
    <div className={PAGE}>
      <PageHeader title="Credits & Extras" subtitle="AI credits, white-label branding, and agency sub-tenants — for choosing or managing your subscription plan, see Billing & Plans." />

      {/* Tabs */}
      <div className="flex gap-1 bg-white border border-[#E5E5E5] rounded-lg p-1 w-fit">
        {validTabs.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`text-xs px-4 py-2 rounded-lg capitalize ${tab === t ? 'bg-[#0176D3] text-white' : 'text-[#5C5C5C] hover:bg-[#F3F3F3]'}`}>
            {t.replace(/-/g, ' ')}
          </button>
        ))}
      </div>

      {usage && tab === 'api-credits' && (
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-5">
          <h2 className="text-sm font-bold text-[#2E2E2E] mb-4">Usage This Month ({usage.month})</h2>
          {[
            { label: 'Messages', key: 'messages', limit: null },
          ].map(({ label, key, limit }) => {
            const used = usage[key] || 0;
            const pct = usagePct(used, limit);
            return (
              <div key={key} className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#5C5C5C]">{label}</span>
                  <span className="text-[#5C5C5C]">{used.toLocaleString()}{limit ? ` / ${limit.toLocaleString()}` : ' (unlimited)'}</span>
                </div>
                {limit && (
                  <div className="bg-[#F3F3F3] rounded-full h-2">
                    <div className={`h-2 rounded-full transition-all ${USAGE_BAR_COLOR(pct)}`} style={{ width: `${pct}%` }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* API Credits */}
      {tab === 'api-credits' && (
        <div className="space-y-4">
          <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-5">
            <h2 className="text-sm font-bold text-[#2E2E2E] mb-1">API Credit Balance</h2>
            <p className="text-xs text-[#5C5C5C] mb-4">
              One shared credit pool meters every LLM reply from the support agent and every live call to
              an API Playground tool, external API connector, or MCP tool. 1,000 tokens ≈ 1 credit; each
              external/MCP call is 1 credit. When the balance hits 0, those features stop working until
              you top up — no surprise overage charges.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-[#EEF4FF] rounded-lg p-3">
                <p className="text-xs text-[#0176D3]">Current Balance</p>
                <p className="text-2xl font-bold text-[#0176D3]">{creditBalance ? creditBalance.balance.toLocaleString() : '—'}</p>
              </div>
              <div className="bg-[#F3F3F3] rounded-lg p-3">
                <p className="text-xs text-[#706E6B]">Total Purchased</p>
                <p className="text-lg font-medium text-[#444444]">{creditBalance ? creditBalance.totalPurchased.toLocaleString() : '—'}</p>
              </div>
              <div className="bg-[#F3F3F3] rounded-lg p-3">
                <p className="text-xs text-[#706E6B]">Total Consumed</p>
                <p className="text-lg font-medium text-[#444444]">{creditBalance ? creditBalance.totalConsumed.toLocaleString() : '—'}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-5">
            <h2 className="text-sm font-bold text-[#2E2E2E] mb-4">Buy Credits</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {creditPacks.map(pack => {
                const display = packDisplay(pack);
                return (
                <div key={pack.credits} className="border border-[#E5E5E5] rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-[#2E2E2E]">{pack.credits.toLocaleString()}</p>
                  <p className="text-xs text-[#5C5C5C] mb-3">credits</p>
                  <p className="text-lg font-medium text-[#444444]">
                    {getCurrencySymbol(display.currency)}{display.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-[11px] text-[#706E6B] mb-3">
                    {display.converted ? `billed as $${pack.priceUsd} USD` : 'charged in USD via Stripe'}
                  </p>
                  <button
                    onClick={() => buyCreditPack(pack)}
                    disabled={buyingPack === pack.credits}
                    className="w-full py-2 rounded-lg text-sm font-medium bg-[#0176D3] hover:bg-[#014486] text-white disabled:bg-[#B8DAF3]"
                  >
                    {buyingPack === pack.credits ? 'Buying…' : 'Buy'}
                  </button>
                </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-5">
            <h2 className="text-sm font-bold text-[#2E2E2E] mb-4">Recent Activity</h2>
            {creditTransactions.length === 0 ? (
              <p className="text-xs text-[#5C5C5C] italic">No credit activity yet.</p>
            ) : (
              <div className="slds2-table-wrap"><table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[#706E6B] border-b border-[#E5E5E5]">
                    <th className="pb-2">Date</th>
                    <th className="pb-2">Type</th>
                    <th className="pb-2">Source</th>
                    <th className="pb-2 text-right">Credits</th>
                    <th className="pb-2 text-right">Balance After</th>
                  </tr>
                </thead>
                <tbody>
                  {creditTransactions.map(t => (
                    <tr key={t._id} className="border-b border-[#F3F2F2]">
                      <td className="py-2 text-[#5C5C5C]">{new Date(t.createdAt).toLocaleString()}</td>
                      <td className="py-2 capitalize text-[#444444]">{t.type}</td>
                      <td className="py-2 text-[#5C5C5C] font-mono">{t.source}</td>
                      <td className={`py-2 text-right font-medium ${t.credits > 0 ? 'text-[#2E844A]' : 'text-[#444444]'}`}>{t.credits > 0 ? '+' : ''}{t.credits.toLocaleString()}</td>
                      <td className="py-2 text-right text-[#5C5C5C]">{t.balanceAfter.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </div>
        </div>
      )}

      {/* White-label */}
      {tab === 'white-label' && (
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-6 space-y-4 max-w-2xl">
          <h2 className="text-sm font-bold text-[#2E2E2E]">White-Label Branding</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { label: 'Brand Name', key: 'brandName', type: 'text' },
              { label: 'Logo URL', key: 'logoUrl', type: 'text' },
              { label: 'Favicon URL', key: 'faviconUrl', type: 'text' },
              { label: 'Custom Domain', key: 'customDomain', type: 'text' },
              { label: 'Support Email', key: 'supportEmail', type: 'email' },
              { label: 'Markup %', key: 'markupPercent', type: 'number' },
            ].map(({ label, key, type }) => (
              <div key={key}>
                <label className="text-xs text-[#5C5C5C] mb-1 block">{label}</label>
                <input type={type} value={wl[key] || ''} onChange={e => setWl(w => ({ ...w, [key]: e.target.value }))}
                  className="w-full border border-[#D8DDE6] rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3]" />
              </div>
            ))}
            <div>
              <label className="text-xs text-[#5C5C5C] mb-1 block">Primary Color</label>
              <div className="flex gap-2">
                <input type="color" value={wl.primaryColor} onChange={e => setWl(w => ({ ...w, primaryColor: e.target.value }))} className="w-10 h-10 rounded border border-[#D8DDE6] cursor-pointer focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
                <input value={wl.primaryColor} onChange={e => setWl(w => ({ ...w, primaryColor: e.target.value }))} className="flex-1 border border-[#D8DDE6] rounded px-3 py-1.5 text-sm focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#5C5C5C] mb-1 block">Accent Color</label>
              <div className="flex gap-2">
                <input type="color" value={wl.accentColor} onChange={e => setWl(w => ({ ...w, accentColor: e.target.value }))} className="w-10 h-10 rounded border border-[#D8DDE6] cursor-pointer focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
                <input value={wl.accentColor} onChange={e => setWl(w => ({ ...w, accentColor: e.target.value }))} className="flex-1 border border-[#D8DDE6] rounded px-3 py-1.5 text-sm focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#5C5C5C] cursor-pointer">
            <input type="checkbox" checked={wl.hideParentBranding} onChange={e => setWl(w => ({ ...w, hideParentBranding: e.target.checked }))} />
            Hide parent platform branding
          </label>
          <div>
            <label className="text-xs text-[#5C5C5C] mb-1 block">Custom CSS</label>
            <textarea value={wl.customCss} onChange={e => setWl(w => ({ ...w, customCss: e.target.value }))}
              rows={4} className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0176D3]" placeholder="/* custom styles */" />
          </div>
          <button onClick={saveWhiteLabel} disabled={loading === 'wl'}
            className="bg-[#0176D3] hover:bg-[#014486] text-white px-6 py-2 rounded text-sm font-medium disabled:bg-[#B8DAF3]">
            {loading === 'wl' ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}

      {/* Sub-tenants */}
      {tab === 'sub-tenants' && (
        <div className="space-y-4">
          <form onSubmit={createSubTenant} className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-5 flex gap-3 items-end flex-wrap">
            {[{ label: 'Name', key: 'name' }, { label: 'Email', key: 'email' }, { label: 'Domain (optional)', key: 'domain' }].map(({ label, key }) => (
              <div key={key} className="flex-1 min-w-[160px]">
                <label className="text-xs text-[#5C5C5C] mb-1 block">{label}</label>
                <input value={newSub[key]} onChange={e => setNewSub(s => ({ ...s, [key]: e.target.value }))} required={key !== 'domain'}
                  className="w-full border border-[#D8DDE6] rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3]" />
              </div>
            ))}
            <button type="submit" className="bg-[#0176D3] hover:bg-[#014486] text-white px-4 py-2 rounded text-sm font-medium whitespace-nowrap">
              + Add Sub-Tenant
            </button>
          </form>

          <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F3F3F3] border-b border-[#E5E5E5]">
                <tr>{['Name', 'Email', 'Domain', 'Created'].map(h => <th key={h} className="text-left px-4 py-3 text-xs font-medium text-[#5C5C5C]">{h}</th>)}</tr>
              </thead>
              <tbody>
                {subTenants.map(st => (
                  <tr key={st._id} className="border-b border-[#E5E5E5] hover:bg-[#F3F3F3]">
                    <td className="px-4 py-2.5 font-medium text-[#2E2E2E]">{st.name}</td>
                    <td className="px-4 py-2.5 text-[#5C5C5C]">{st.email}</td>
                    <td className="px-4 py-2.5 text-[#706E6B]">{st.domain || '—'}</td>
                    <td className="px-4 py-2.5 text-[#706E6B]">{new Date(st.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
                {subTenants.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-[#706E6B] text-sm">No sub-tenants yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
